import { useEffect, useMemo, useState, type PropsWithChildren } from "react";

import clsx from "clsx";
import { orderBy } from "lodash";
import moment from "moment";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { PieChart, QuickSelectInfoButton, Spinner } from "@components/v2";
import { useQueryMetadata } from "@components/v2/modals";
import {
  useSalesV4Context,
  useTableRowFilters,
  useTableStateDimensions,
} from "@features/sales-v4/hooks";
import { type TableState } from "@features/sales-v4/types";
import { selectedTabToLevel } from "@features/sales-v4/utils";
import { SALES_METRICS_REPORT_V4 } from "@gql/sales-page-v4";
import { TOUCHPOINTS_SUMMARY } from "@gql/touchpoints";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type GetSalesMetricsReportV4,
  type GetSalesMetricsReportV4Variables,
} from "@north-beam/nb-client-side";
import {
  type GetTouchpointsSummary,
  type GetTouchpointsSummaryVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { formatting, type SalesMetricRow } from "@north-beam/nb-common";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

export const Tooltip = ({
  row,
  tableState,
  children,
}: PropsWithChildren<{
  row: SalesMetricRow;
  tableState: TableState;
}>) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <GenericTooltip
      noInfoCircle
      overlayClassName={clsx(
        "p-0 rounded-lg border-none shadow-[0_4px_8px] shadow-v2-black/15%",
      )}
      arrowClassName="bg-v2-secondary"
      placement="top"
      mouseEnterDelay={500}
      content={
        <TooltipContent
          row={row}
          tableState={tableState}
          setIsLoading={setIsLoading}
        />
      }
    >
      <div
        className={clsx({
          "cursor-wait": isLoading,
          "cursor-default": !isLoading,
        })}
      >
        {children}
      </div>
    </GenericTooltip>
  );
};

const TooltipContent = ({
  row,
  tableState,
  setIsLoading,
}: {
  row: SalesMetricRow;
  tableState: TableState;
  setIsLoading: (isLoading: boolean) => void;
}) => {
  const { user } = useUser();
  const { isDemoMode } = usePrefs();
  const { hasFeatureFlag } = useFeatureFlags();
  const { designElementManager } = useDesignElementManagerV2();
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRangeInterval, timeGranularity } = useSalesV4Context();
  const { idColumn, nameColumn, platformDimension, breakdownDimension } =
    useTableStateDimensions(tableState);
  const { addQueryMetadata } = useQueryMetadata();

  const {
    dimensionIds,
    metricFilters,
    breakdownFilters,
    campaignFilters,
    adsetFilters,
    adFilters,
    isSummary,
  } = useTableRowFilters({
    tableState,
    row,
  });

  const [localAttributionWindow, setLocalAttributionWindow] =
    useState(attributionWindow);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(true);
  const [summaryData, setSummaryData] = useState<SalesMetricRow[]>([]);
  const [rowData, setRowData] = useState<SalesMetricRow[]>([]);

  const { touchpointsSummaryRowFilter } = useMemo(() => {
    let _touchpointsSummaryRowFilter = null;

    const id = idColumn.getValue(row);

    if (id) {
      _touchpointsSummaryRowFilter = { "==": [{ var: idColumn.id }, id] };
    }

    return {
      touchpointsSummaryRowFilter: _touchpointsSummaryRowFilter,
    };
  }, [row, idColumn]);

  useNorthbeamQuery<GetSalesMetricsReportV4, GetSalesMetricsReportV4Variables>(
    SALES_METRICS_REPORT_V4,
    {
      skip: !isSummary,
      variables: {
        attributionModel,
        attributionWindow: localAttributionWindow,
        accountingMode,
        dateRange: dateRangeInterval,
        level: selectedTabToLevel(tableState.selectedTab),
        timeGranularity,
        metricIds: [
          "totalTouchpoints",
          "orderCount",
          "firstTouchConversionDiffHours",
        ],
        dimensionIds: [breakdownDimension.id],
        metricFilters,
        breakdownFilters,
        campaignFilters,
        adsetFilters,
        adFilters,
      },
      onCompleted(data) {
        setLoading(false);
        setReloading(false);
        setSummaryData(data.me.salesMetricsReportV4.actual);
        addQueryMetadata({
          context: "Revenue",
          topic: "Touchpoints Tooltip Summary Data",
          metadata: data.me.salesMetricsReportV4.queryMetadata,
        });
      },
    },
  );

  useNorthbeamQuery<GetTouchpointsSummary, GetTouchpointsSummaryVariables>(
    TOUCHPOINTS_SUMMARY,
    {
      skip: isSummary,
      variables: {
        attributionModel,
        attributionWindow: localAttributionWindow,
        accountingMode,
        dateRange: dateRangeInterval,
        level: selectedTabToLevel(tableState.selectedTab),
        timeGranularity,
        metricIds: (metricFilters ?? []).map((e) => e.metricId),
        dimensionIds,
        metricFilters: isSummary ? metricFilters : null,
        breakdownFilters,
        campaignFilters: isSummary ? campaignFilters : null,
        adsetFilters: isSummary ? adsetFilters : null,
        adFilters: isSummary ? adFilters : null,
        touchpointsSummaryDimensionIds: isSummary ? [] : dimensionIds,
        touchpointsSummaryRowFilter,
      },
      onCompleted(data) {
        setLoading(false);
        setReloading(false);
        setRowData(data.me.touchpointsSummary.data);
        addQueryMetadata({
          context: "Revenue",
          topic: "Touchpoints Tooltip Row Data",
          metadata: data.me.touchpointsSummary.queryMetadata,
        });
      },
    },
  );

  useEffect(() => {
    setIsLoading(loading);
  }, [loading, setIsLoading]);

  const chartData = useMemo(() => {
    if (summaryData.length > 0) {
      const filteredData = summaryData.filter(
        (e) => e.metrics.totalTouchpoints && e.metrics.orderCount,
      );
      const totalNumberOfTouchpoints = filteredData.reduce(
        (acc, row) => acc + (row.metrics.totalTouchpoints ?? 0),
        0,
      );
      const totalOrders = filteredData.reduce(
        (acc, row) => acc + (row.metrics.orderCount ?? 0),
        0,
      );

      const avgTouchpointsPerOrder = totalNumberOfTouchpoints / totalOrders;
      const processedTouchpoints = filteredData
        .map((e) => ({
          name: breakdownDimension.getValue(e) ?? "",
          avgTouchpoints:
            ((e.metrics.totalTouchpoints ?? 0) / totalNumberOfTouchpoints) *
            avgTouchpointsPerOrder,
        }))
        .filter((e) => e.avgTouchpoints);

      const sortedTouchpoints = orderBy(
        processedTouchpoints,
        ["avgTouchpoints"],
        ["desc"],
      );

      const mainTouchpoints = sortedTouchpoints.slice(0, 5);
      const restTouchpoints = sortedTouchpoints.slice(5);

      const result = mainTouchpoints.map((e, index) => ({
        label: e.name,
        value: e.avgTouchpoints,
        color:
          designElementManager.getColorForIndex(index) +
          (index === 0 ? "" : "88"),
      }));

      if (restTouchpoints.length > 0) {
        const avgTouchpoints = restTouchpoints.reduce(
          (acc, row) => acc + row.avgTouchpoints,
          0,
        );

        result.push({
          label: "Rest",
          value: avgTouchpoints,
          color: designElementManager.getColorForIndex(result.length) + "88",
        });
      }

      return result;
    } else if (rowData.length > 0) {
      const rowId = idColumn.getValue(row);

      let processedTouchpoints: {
        id: string;
        name: string;
        avgTouchpoints: number;
      }[] = [];

      const filteredData = rowData.filter(
        (e) => e.metrics.totalTouchpoints && e.metrics.orderCount,
      );
      const totalNumberOfTouchpoints = filteredData.reduce(
        (acc, row) => acc + (row.metrics.totalTouchpoints ?? 0),
        0,
      );

      const currentRow = rowData.find((e) => idColumn.getValue(e) === rowId);
      const totalOrders = currentRow?.metrics.orderCount ?? 0;
      const avgTouchpointsPerOrder = totalNumberOfTouchpoints / totalOrders;

      // we use this count since we want to only get the total distinct orders for the current row
      if (idColumn.id === "adKey") {
        for (const rowEl of rowData) {
          if (idColumn.getValue(rowEl) === rowId) {
            processedTouchpoints.push({
              id: idColumn.getValue(rowEl) ?? "",
              name: nameColumn.getValue(rowEl) ?? "",
              avgTouchpoints:
                ((rowEl.metrics.totalTouchpoints ?? 0) /
                  totalNumberOfTouchpoints) *
                avgTouchpointsPerOrder,
            });
          } else {
            const rowElId = platformDimension.getValue(rowEl) ?? "";
            const existing = processedTouchpoints.find((e) => e.id === rowElId);

            if (existing) {
              existing.avgTouchpoints +=
                ((rowEl.metrics.totalTouchpoints ?? 0) /
                  totalNumberOfTouchpoints) *
                avgTouchpointsPerOrder;
            } else {
              processedTouchpoints.push({
                id: rowElId,
                name: rowElId,
                avgTouchpoints:
                  ((rowEl.metrics.totalTouchpoints ?? 0) /
                    totalNumberOfTouchpoints) *
                  avgTouchpointsPerOrder,
              });
            }
          }
        }
      } else {
        processedTouchpoints = rowData.map((e) => ({
          id: idColumn.getValue(e) ?? "",
          name: nameColumn.getValue(e) ?? "",
          avgTouchpoints:
            ((e.metrics.totalTouchpoints ?? 0) / totalNumberOfTouchpoints) *
            avgTouchpointsPerOrder,
        }));
      }

      const currentTouchpoints = processedTouchpoints.filter(
        (e) => e.id === rowId,
      );
      const otherTouchpoints = processedTouchpoints.filter(
        (e) => e.id !== rowId,
      );

      const sortedTouchpoints = [
        ...currentTouchpoints,
        ...orderBy(otherTouchpoints, ["avgTouchpoints"], ["desc"]),
      ];

      const mainTouchpoints = sortedTouchpoints.slice(0, 5);
      const restTouchpoints = sortedTouchpoints.slice(5);

      const result = mainTouchpoints.map((e, index) => ({
        label: generateAnonBase64Hash({
          value: e.name,
          prefix: tableState.selectedTab,
          doAnon: isDemoMode && tableState.selectedTab !== "breakdown",
        }),
        value: e.avgTouchpoints,
        color:
          designElementManager.getColorForIndex(index) +
          (index === 0 ? "" : "88"),
      }));

      if (restTouchpoints.length > 0) {
        const avgTouchpoints = restTouchpoints.reduce(
          (acc, row) => acc + row.avgTouchpoints,
          0,
        );

        result.push({
          label: "Rest",
          value: avgTouchpoints,
          color: designElementManager.getColorForIndex(result.length) + "88",
        });
      }

      return result;
    }

    return [];
  }, [
    summaryData,
    rowData,
    breakdownDimension,
    designElementManager,
    idColumn,
    row,
    nameColumn,
    platformDimension,
    tableState.selectedTab,
    isDemoMode,
  ]);

  const tableData = useMemo(() => {
    const formatDuration = (value: number) => {
      const duration = moment.duration(value, "hours");

      if (hasFeatureFlag("fixTouchpointsTimeToPurchaseFormatting")) {
        const res = [];

        if (duration.get("years") > 0) {
          res.push(`${duration.get("years")}y`);
        }
        if (duration.get("months") > 0) {
          res.push(`${duration.get("months")}m`);
        }
        if (duration.get("days") > 0) {
          res.push(`${duration.get("days")}d`);
        }
        if (duration.get("hours") > 0) {
          res.push(`${duration.get("hours")}h`);
        }
        if (duration.get("minutes") > 0 || res.length === 0) {
          res.push(`${duration.get("minutes")}m`);
        }

        return res.join(" ");
      } else {
        return duration.get("hours") > 0
          ? `${duration.get("hours")}h ${duration.get("minutes")}m`
          : `${duration.get("minutes")}m`;
      }
    };

    const result = [];
    if (summaryData.length > 0) {
      const filteredData = summaryData.filter(
        (e) => e.metrics.totalTouchpoints && e.metrics.orderCount,
      );

      const totalTouchpoints = filteredData.reduce(
        (acc, row) => acc + (row.metrics.totalTouchpoints ?? 0),
        0,
      );
      const totalTimeToPurchase = filteredData.reduce(
        (acc, row) => acc + (row.metrics.firstTouchConversionDiffHours ?? 0),
        0,
      );
      const totalOrders = filteredData.reduce(
        (acc, row) => acc + (row.metrics.orderCount ?? 0),
        0,
      );

      result.push({
        label: "Avg touchpoints per order",
        value: formatting.formatDecimal(totalTouchpoints / totalOrders, {}),
      });
      if (accountingMode === "accrual") {
        result.push({
          label: "Time to purchase",
          value: formatDuration(totalTimeToPurchase / totalOrders),
        });
      }
    } else if (rowData.length > 0) {
      const rowId = idColumn.getValue(row);

      const totalNumberOfTouchpoints = rowData.reduce(
        (acc, row) => acc + (row.metrics.totalTouchpoints ?? 0),
        0,
      );
      const totalTimeToPurchase = rowData.reduce(
        (acc, row) => acc + (row.metrics.firstTouchConversionDiffHours ?? 0),
        0,
      );

      const currentRow = rowData.find((e) => idColumn.getValue(e) === rowId);

      if (rowId && currentRow) {
        const currentRowTouchpoints = currentRow.metrics.totalTouchpoints ?? 0;
        // we use this count since we want to only get the total distinct orders for the current row
        const totalOrders = currentRow.metrics.orderCount ?? 0;
        const avgTouchpointsPerOrder = totalNumberOfTouchpoints / totalOrders;

        result.push({
          label: "Avg touchpoints per order",
          value: formatting.formatDecimal(avgTouchpointsPerOrder, {}),
          share: formatting.formatPercentage(1, { mantissa: 0 }),
        });

        const otherTouchpoints =
          totalNumberOfTouchpoints - currentRowTouchpoints;
        result.push({
          label: `Attributed to this ${tableState.selectedTab}`,
          value: formatting.formatDecimal(
            (currentRowTouchpoints / totalNumberOfTouchpoints) *
              avgTouchpointsPerOrder,
            {},
          ),
          share: formatting.formatPercentage(
            currentRowTouchpoints / totalNumberOfTouchpoints,
            { mantissa: 1 },
          ),
        });

        result.push({
          label: `Shared with other ${tableState.selectedTab}s`,
          value: formatting.formatDecimal(
            (otherTouchpoints / totalNumberOfTouchpoints) *
              avgTouchpointsPerOrder,
            {},
          ),
          share: formatting.formatPercentage(
            otherTouchpoints / totalNumberOfTouchpoints,
            { mantissa: 1 },
          ),
        });
        if (accountingMode === "accrual") {
          result.push({
            label: "Time to purchase",
            value: formatDuration(totalTimeToPurchase / totalOrders),
          });
        }
      }
    }

    return result;
  }, [
    summaryData,
    rowData,
    accountingMode,
    idColumn,
    row,
    tableState.selectedTab,
  ]);

  if (loading) {
    return (
      <div
        className={clsx(
          "p-4 bg-v2-secondary rounded-lg flex items-center justify-center",
          "w-[420px] h-[440px]",
        )}
      >
        <Spinner className="text-v2-action" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-v2-secondary rounded-lg w-[420px] text-v2-primary">
      <div className="text-sm font-semibold mb-2">Touchpoint</div>
      <div className="flex gap-2 mb-3">
        <QuickSelectInfoButton
          data-autofocus={
            localAttributionWindow === "infinity" ? true : undefined
          }
          className="flex-1"
          onClick={() => {
            setReloading(true);
            setLocalAttributionWindow("infinity");
          }}
          selected={localAttributionWindow === "infinity"}
        >
          {localAttributionWindow === "infinity" && reloading && (
            <Spinner className="text-v2-action w-3 h-3" />
          )}
          Lifetime
        </QuickSelectInfoButton>
        {accountingMode === "accrual" && (
          <QuickSelectInfoButton
            data-autofocus={
              localAttributionWindow === attributionWindow ? true : undefined
            }
            className="flex-1"
            onClick={() => {
              setReloading(true);
              setLocalAttributionWindow(attributionWindow);
            }}
            selected={localAttributionWindow === attributionWindow}
          >
            {localAttributionWindow === attributionWindow && reloading && (
              <Spinner className="text-v2-action w-3 h-3" />
            )}
            {user.attributionMethodEnum.getLabel(attributionModel)} /{" "}
            {attributionWindow} day
          </QuickSelectInfoButton>
        )}
      </div>
      <div className="mb-4">
        <div className="text-xs">
          <span className="inline-block align-middle">
            Total customer journey for orders attributed to
          </span>{" "}
          <span className="inline-block align-middle font-semibold truncate max-w-[260px]">
            {generateAnonBase64Hash({
              value: nameColumn.getValue(row),
              prefix: tableState.selectedTab,
              doAnon: isDemoMode && tableState.selectedTab !== "breakdown",
            })}
          </span>
        </div>
        <div className="w-full flex items-center justify-center py-4">
          <PieChart
            data={chartData}
            borderWidth={0}
            hoverBorderWidth={0}
            showTooltip={chartData.length > 0}
            animation={false}
          />
        </div>
        <div
          className={clsx(
            "flex items-center justify-center flex-wrap gap-2 text-2xs text-v2-secondary",
          )}
        >
          {(chartData.length === 0
            ? [{ label: "No Data", color: "#dddddd" }]
            : chartData
          ).map((row) => {
            return (
              <div key={row.label} className="flex items-center gap-1">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: row.color }}
                ></span>
                <span className="max-w-[100px] truncate">{row.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      {chartData.length > 0 && (
        <>
          <div className="mb-2 font-semibold">
            Average Customer Journey (
            {localAttributionWindow === "infinity"
              ? "Lifetime"
              : `${localAttributionWindow} day lookback`}
            )
          </div>
          <div className="border border-v2-tertiary rounded bg-v2-primary">
            {tableData.map((row, index) => {
              return (
                <div key={index} className="h-10 flex items-stretch">
                  <div
                    className={clsx(
                      "border-r border-v2-tertiary px-4 flex shrink-0 items-center w-[240px]",
                      { "border-b": index !== tableData.length - 1 },
                    )}
                  >
                    {row.label}
                  </div>
                  <div
                    className={clsx(
                      "shrink-0 flex-1 px-4 flex items-center justify-end gap-1 text-right",
                      {
                        "border-b border-v2-tertiary":
                          index !== tableData.length - 1,
                      },
                    )}
                  >
                    {row.share && (
                      <div
                        className={clsx(
                          "font-mono text-2xs rounded h-5 flex items-center px-1.5 text-nowrap",
                          "text-v2-action-positive-hover bg-v2-alien/400/10%",
                        )}
                      >
                        {row.share}
                      </div>
                    )}
                    {row.value}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
